import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface UpdateState {
    available: boolean;
    currentVersion: string;
    availableVersion: string;
    isUpdating: boolean;
    isDismissed: boolean;
    lastChecked: Date | null;
    error: string | null;
}

@Injectable({
    providedIn: 'root',
})
export class AppUpdateService {
    private readonly swUpdate = inject(SwUpdate);

    private readonly _updateState = signal<UpdateState>({
        available: false,
        currentVersion: '',
        availableVersion: '',
        isUpdating: false,
        isDismissed: false,
        lastChecked: null,
        error: null,
    });

    private readonly _autoUpdateEnabled = signal<boolean>(false);
    private readonly _checkInterval = signal<number>(5 * 60 * 1000);

    readonly updateState = this._updateState.asReadonly();

    readonly isUpdateAvailable = computed(() => this._updateState().available);
    readonly isUpdating = computed(() => this._updateState().isUpdating);
    readonly canUpdate = computed(() => this._updateState().available && !this._updateState().isUpdating);
    readonly isDismissed = computed(() => this._updateState().isDismissed);

    constructor() {
        this.initializeUpdateListener();
        this.setupAutoUpdateEffect();
        this.setupPeriodicCheck();
    }

    private initializeUpdateListener() {
        if (!this.swUpdate.isEnabled) return;

        this.swUpdate.versionUpdates
            .pipe(
                filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
                takeUntilDestroyed()
            )
            .subscribe((evt) => {
                this._updateState.update((state) => ({
                    ...state,
                    available: true,
                    availableVersion: evt.latestVersion.hash,
                    currentVersion: evt.currentVersion.hash,
                    lastChecked: new Date(),
                    isUpdating: false,
                    error: null,
                }));
            });

        this.swUpdate.unrecoverable.pipe(takeUntilDestroyed()).subscribe((event) => {
            this._updateState.update((state) => ({
                ...state,
                error: `Unrecoverable state: ${event.reason}`,
                isUpdating: false,
            }));
        });
    }

    private setupAutoUpdateEffect() {
        effect(() => {
            const autoEnabled = this._autoUpdateEnabled();
            const updateAvailable = this._updateState().available;

            if (autoEnabled && updateAvailable && !this._updateState().isUpdating) {
                this.activateUpdate();
            }
        });
    }

    private setupPeriodicCheck() {
        effect(() => {
            const interval = this._checkInterval();

            const intervalId = setInterval(() => {
                this.checkForUpdate();
            }, interval);

            return () => clearInterval(intervalId);
        });
    }

    async checkForUpdate(): Promise<boolean> {
        if (!this.swUpdate.isEnabled) return false;

        try {
            this._updateState.update((state) => ({
                ...state,
                error: null,
                lastChecked: new Date(),
            }));

            const updateFound = await this.swUpdate.checkForUpdate();

            if (!updateFound) {
                this._updateState.update((state) => ({
                    ...state,
                    available: false,
                }));
            }

            return updateFound;
        } catch (error) {
            console.log(`PWA: Check for update failed ${error}`);
            this._updateState.update((state) => ({
                ...state,
                error: `Check failed: ${error}`,
            }));
            return false;
        }
    }

    async activateUpdate(): Promise<boolean> {
        if (!this.canUpdate()) return false;

        try {
            this._updateState.update((state) => ({
                ...state,
                isUpdating: true,
                error: null,
            }));
            await this.swUpdate.activateUpdate();
            this.applicationReload();
            return true;
        } catch (error) {
            this._updateState.update((state) => ({
                ...state,
                isUpdating: false,
                error: `Update failed: ${error}`,
            }));
            return false;
        }
    }

    dismissUpdate(): void {
        if (this.isDismissed()) {
            return;
        }
        this._updateState.update((state) => ({
            ...state,
            isDismissed: true,
        }));
    }

    autoUpdate(enabled: boolean): void {
        if (this._autoUpdateEnabled() === enabled) {
            return;
        }
        this._autoUpdateEnabled.update(() => enabled);
    }

    clearError(): void {
        this._updateState.update((state) => ({
            ...state,
            error: null,
        }));
    }

    simulateNewVersion(newVersion: string): void {
        this._updateState.update((state) => ({
            ...state,
            available: true,
            availableVersion: newVersion,
            lastChecked: new Date(),
            isUpdating: false,
            error: null,
        }));
        console.log('PWA: Simulating new version:', this._updateState());
    }

    setCurrentVersion(version: string): void {
        if (version === '' || version === 'local') {
            version = '1.0.0-1';
        }
        this._updateState.update((state) => ({
            ...state,
            currentVersion: version,
        }));
    }

    applicationReload(): void {
        window.location.reload();
    }
}
